import { api } from './api'
import axios from 'axios'
import { ExtendedFile } from '@components/FileUpload/UploaderDialog'
import { BreakRecordItem, TimeRecordItem } from './time.services'
import { URI } from '@constants/uri.constants'
import { SystemSettingsItem } from './settings.services'

export type getPresignedUrlResponse = {
  url: string
  file_url: string
  file_name: string
  key: string
}

export type AppInitResponse = {
  data: AppInit
}

export type AvatarUrls = {
  avatar_cover: string | null
  avatar_medium: string | null
  avatar_thumbnail: string | null
}

export type MissingReportItem = {
  id: string
  type: 'daily_report_missing'
  attributes: {
    work_date: string
  }
}

export type CompanyItem = {
  id: string
  type: string
  attributes: {
    company_type: 'main'
    redirect_flg: boolean
    company: {
      data: {
        id: string
        type: 'company'
        attributes: {
          name: string
          uid: string
          domain: string
          base_url: string
          enabled_flg: true
        }
      }
    }
  }
}

export type AppInit = {
  id: string
  type: string
  attributes: {
    time_records: {
      data: TimeRecordItem
    }
    break_records: {
      data: BreakRecordItem[]
    }
    company_settings: {
      data: SystemSettingsItem[]
    }
    system_timezone: string
    current_user: { uid: string; email: string; id: number }
    daily_report_missing_dates: {
      data: MissingReportItem[]
    }
    possible_my_time_break_morning: number
    companies: {
      data: CompanyItem[]
    }
  }
}

export type SwitchCompanyParams = {
  company_uid: string
}

export type SwitchCompanyResponse = {
  data: {
    id: string
    type: 'app_init'
    attributes: {
      current_user: {
        id: number
        uid: string
        email: string
      }
      companies: {
        data: CompanyItem[]
      }
    }
  }
}

export const uploadImage = async (
  uri: string,
  file: ExtendedFile,
  type: string,
  cb?: (progress: number) => void,
  project_id?: string
) => {
  const { data: presignedResponse } = await api.post<getPresignedUrlResponse>(
    uri,
    {
      file_name: file.name,
      content_type: type,
      mime_type: file.type,
      project_id: project_id,
    }
  )
  await axios.put(presignedResponse.url, file, {
    headers: {
      'Content-Type': file.type,
    },
    onUploadProgress(e: ProgressEvent) {
      const percentCompleted = Math.round((e.loaded * 100) / e.total)
      cb && cb(percentCompleted)
    },
  })
  return presignedResponse
}

export const initService = async (): Promise<AppInitResponse> => {
  const { data } = await api.get<AppInitResponse>(URI.INIT)
  return data
}

export const switchCompanyService = async (
  params: SwitchCompanyParams
): Promise<SwitchCompanyResponse> => {
  const { data } = await api.post<SwitchCompanyResponse>(
    URI.SWITCH_COMPANY,
    params
  )
  return data
}

export interface QuizItem {
  id: string
  step_number: number
  fields: QuizField[]
  is_last: boolean
  title?: string
  description?: string
  image?: string
}

export enum FORMTYPE {
  SELECT = 'select',
  DATE_RANGE = 'date_range',
  NUMBER = 'number',
  RADIO = 'radio',
  DATE_PICKER = 'date_picker',
  MULTI_SELECT = 'multi_select',
  TIMEPICKER = 'time_picker',
  CHIP_SELECT = 'chip_select',
  CHIP_MULTI_SELECT = 'chip_multi_select',
}

export type QuizOption = { label: string; value: any; icon?: string }

export interface QuizField {
  order_id: number
  field_id: string
  question: string
  description?: string
  form_type: FORMTYPE
  format_type: string
  options?: QuizOption[]
  dependant_on?: string
  show_if?: { field_id: string; value: string }
  formHelp?: string
  default_value?: string
  validation_rule?: string
}

export type Section = {
  id: string
  name: string
  description: string
  quiz_items: QuizItem[]
  section_id: SectionsIds
  is_complete: boolean
}

export enum SectionsIds {
  SURVEY_SETUP = 'survey_setup',
  COMPANY_SETUP = 'company_setup',
  INIT_POSITIONS = 'init_positions',
  INIT_TAGS = 'init_tags',
}

export type Quiz = {
  sections: Section[]
}

export type Answer = {
  field_id: string
  value: number | string | string[]
}

export type AnswerPayload = {
  fields: Answer[]
}

export type PositionAnswer = {
  name: string
  short_name: string
}
export type PositionAnswerPayload = {
  positions: PositionAnswer[]
}

export type TagAnswer = {
  name: string
  description: string
}
export type TagAnswerPayload = {
  tags: TagAnswer[]
}

export const getQuizService = async (): Promise<Quiz> => {
  const { data } = await api.get<Quiz>(URI.QUIZ)
  return data
}

export const postQuizService = async (payload: AnswerPayload): Promise<any> => {
  const data = await api.post<any>(URI.COMPANY_SETUP, payload)
  return data
}

export const setInitTagService = async (
  payload: TagAnswerPayload
): Promise<Quiz> => {
  const { data } = await api.post<Quiz>(URI.SET_INIT_TAGS, payload)
  return data
}

export const setInitPositionService = async (
  payload: PositionAnswerPayload
): Promise<Quiz> => {
  const { data } = await api.post<Quiz>(URI.SET_INIT_POSITION, payload)
  return data
}

export const surveyService = async (payload: AnswerPayload): Promise<Quiz> => {
  const { data } = await api.post<Quiz>(URI.SURVEY_SETUP, payload)
  return data
}
